import isObject from 'lodash.isobject';
import transform from 'lodash.transform';
import isEqual from 'lodash.isequal';
import cloneDeep from 'lodash.clonedeep';
import { stringIsNullOrEmpty } from './strings';

//wraps setProperty
export function setPropertyWrapper(state, newState) {
    for (let fieldName in newState) {
        setProperty(state, fieldName, newState[fieldName]);
    }
}

export function getProperty(object, property, create) {
    if (!object || !property) return undefined;
    return nestedGet(object, property.split('.'), create);
}

//note: set may not work if the end property is an array
//example. This should work:
//  some[0].property = value | works
//  some.properties[0] = value | may not work
export function setProperty(object, property, value) {
    if (!object || !property) return undefined;
    const properties = property.split('.');
    const finalProperty = properties.pop();
    const result = nestedGet(object, properties, true);
    return result && finalProperty ? (result[finalProperty] = value) : undefined;
}

const squareBracketPattern = /\[+(.*?)\]+/g;

function nestedGet(object, properties, create) {
    object = object || window;

    for (let i = 0; !isNullOrUndefined(object); i++) {
        let property = properties[i];
        if (isNullOrUndefined(property)) break;

        let match = property.match(squareBracketPattern);
        if (match && match.length === 1 && match[0].length > 0) {
            const index = match[0].substring(1, match[0].length - 1);
            property = property.replace('[' + index + ']', '');
            const array = object[property];
            if (array) object = array ? object[property][index] : create ? (object[property] = {}) : undefined;
            else object = create ? [] : undefined;
        } else {
            object = property in object ? object[property] : create ? (object[property] = {}) : undefined;
        }
    }

    return object;
}

/**
 * detects whether the page has loaded with form data for the first time
 * useful if you want to set obj state properties after the form loads
 */
export function isFirstLoad(obj, lastProps) {
    const oldForm = lastProps.form;
    const newForm = obj.props.form;
    return newForm && oldForm && oldForm.loading && !newForm.loading;
}

/**
 * retrieves an array that uses edges. returns an empty edge array if not present
 */
export const getArray = (context, arrayName) => {
    const { form } = context.props;
    if (!form) return [];
    return getProperty(form.fields, arrayName) || [];
};

/**
 * retrieves an array that uses edges. returns an empty edge array if not present
 */
export const getObjectFromArray = (context, arrayName, index = 0) => {
    const array = getArray(context, arrayName);
    if (array.length > 0 && index < array.length) {
        return array[index];
    }
    return null;
};

/**
 * groups a collection by a property into a map
 */
export const groupBy = (list, getKeyFunc, getValueFunc, asArray = false) => {
    const map = new Map();
    for (let x = 0; x < list.length; x++) {
        const key = getKeyFunc(list[x]);
        const collection = map.get(key);
        const value = getValueFunc ? getValueFunc(list[x], x) : list[x];
        if (!collection) {
            map.set(key, [value]);
        } else {
            collection.push(value);
        }
    }

    if (!asArray) return map;

    const array = [];
    for (let [key, value] of map) array.push({ key, value });

    return array;
};

/**
 * check if a value exists, including zero or false or empty string
 */
export const isNullOrUndefined = obj => {
    return obj === undefined || obj === null;
};

/**
 * Apollo includes the `__typename` field on the data returned from queries
 * We recursively iterate over the given data to remove all `__typename` fields
 * so that the object is sanitized before before saving it to GQL
 *
 * Also destroy ghost objects with ID = 0, go Pacman!
 *
 * @param {*} input
 * @param {boolean} keepGhosts retain empty objects (e.g. if a has_one was removed)
 */
export const deleteTypeName = (input, keepGhosts = false) => {
    Object.keys(input).forEach(key => {
        if (key === '__typename') {
            try {
                delete input[key];
            } catch (e) {
                console.warn(e);
            }
        } else if (isObject(input[key])) {
            if (input[key].ID && (input[key].ID === 0 || input[key].ID === '0')) {
                if (!!keepGhosts) {
                    input[key] = { ID: '0' };
                } else {
                    delete input[key];
                }
            } else deleteTypeName(input[key]);
        }
    });
    return input;
};

export const flattenKeys = (obj, ns, hash) => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        let keypath = ns ? `${ns}.${key}` : key;
        if (typeof value === 'object' && value !== null) {
            return flattenKeys(value, keypath, acc);
        }
        acc[keypath] = value;
        return acc;
    }, hash || {});
};

export const resetAllProperties = obj => {
    clearNestedValues(obj);
};

const clearValue = obj => {
    if (obj === null || obj === undefined) return null;

    var type = typeof obj;

    if (type === 'boolean') return false;

    if (type === 'string') return null;

    if (type === 'number') return 0;

    if (Array.isArray(obj)) return [];

    clearNestedValues(obj);

    return obj;
};

const clearNestedValues = obj => {
    if (typeof obj !== 'object') return;
    Object.keys(obj).forEach(key => (obj[key] = clearValue(obj[key])));
};

export const round = (value, decimals = 2) => {
    var factor = Math.pow(10, decimals);
    var result = Math.round(value * factor) / factor;
    return result;
};

export const orientationMode = {
    horizontal: 0,
    vertical: 1
};

export const updateNested = (original, update) => {
    if (update === null) return update;
    const update_type = typeof update;
    if (update_type === 'object') {
        if (Array.isArray(update)) {
            original = [];
            update.forEach(entry => {
                const entry_type = typeof entry;
                if (entry_type === 'object') {
                    original.push(updateNested(null, entry));
                } else {
                    original.push(entry);
                }
            });
        } else {
            if (isNullOrUndefined(original)) original = {};
            for (let property in update) {
                const property_type = typeof update[property];
                if (property_type === 'object') {
                    if (
                        !!update[property] &&
                        !!update[property]['definitions'] &&
                        !!update[property]['kind'] &&
                        !!update[property]['loc']
                    ) {
                        //We don't need to update fragments, just overwrite it with new one
                        original[property] = update[property];
                    }
                    original[property] = updateNested(original[property], update[property]);
                } else if (property === undefined) {
                    delete original[property];
                } else {
                    original[property] = update[property];
                }
            }
        }
    } else {
        return update;
    }
    return original;
};

/**
 * counts to a specific number
 * @param {*} number
 */
export const countTo = number => {
    const arr = [];
    for (let x = 0; x < number; x++) arr.push(x);
    return arr;
};

/* refer to FM functions.js */

/**
 * Flattens edges and nodes in a connection data structure.
 * i.e. data => Children => edges => node becomes data => Children
 */
export const flattenConnection = (data, propertyName) => {
    if (!data || !data[propertyName] || data[propertyName].edges === undefined) return;
    data[propertyName] = data[propertyName].edges.map(e => e.node);
};

/**
 * turns collection of objects into their respective ids
 * used when there's a relation
 */
export const reduceToIds = array => {
    for (let x = 0; x < array.length; x++) {
        array[x] = {
            ID: array[x].ID
        };
    }
};

/**
 * turns an object to an id if it exists
 */
export const reduceToId = (obj, propertyName) => {
    if (!obj[propertyName]) return;

    obj[`${propertyName}ID`] = obj[propertyName].ID;
    delete obj[propertyName];
};

/* checks if a gql object is null or undefined
 */
export const isRelatedObjectUndefined = obj => {
    return isNullOrUndefined(obj) || obj.ID === '0' || isNullOrUndefined(obj.ID);
};

/**
 * Returns a new function which when invoked will execute all of the given functions
 * and propagating the same parameters into each function.
 */
export function applyAll(...functions) {
    return (...params) => {
        functions.forEach(func => typeof func === 'function' && func(...params));
    };
}

/**
 *
 * @param e the event fired
 * @param context the form object, eg. TextField
 * @param isTickbox for checkboxes
 * @param isNumber for numeric values, otherwise you get a string
 */
export const onChangeWrapper = (e, context, isTickbox = false, isNumber = false) => {
    /*
     * onChanging: function that will execute BEFORE the value is applied. If e.preventDefault() is called, this function will terminate
     * onChange: function that will execute AFTER the value is applied
     * valueField: get a nested result from the target
     * emptyValue: replace empty string with null
     */
    const { onChanging, onChange, name, form, valueField, emptyValue = null } = context.props;

    if (onChanging) onChanging(e);

    if (e.defaultPrevented) return;
    const valToCheck = !!isTickbox ? e.target.checked : e.target.value;
    let value = !stringIsNullOrEmpty(valueField)
        ? !isNullOrUndefined(valToCheck)
            ? valToCheck[valueField]
            : null
        : valToCheck;

    if (isNullOrUndefined(value)) value = emptyValue;

    if (isNumber) {
        const newValue = parseFloat(value);
        value = isNaN(newValue) ? 0 : newValue;
    }

    if (!isNullOrUndefined(form)) {
        if (!form.getValidation) throw new Error(name + ' form does not contain validation function!');

        if (!stringIsNullOrEmpty(name)) form.setField({ [name]: value });

        if (form && form.getValidation(name, true).shouldUpdate) context.forceUpdate();
    } else {
        context.setState({ value });
    }

    if (onChange) onChange(e, value);
};

/**
 * Super loose comparisons...
 * If the first value is undefined, return false.
 * If the first value is pretty much the same as the second value, return false.
 * '', null and undefined are equal.
 * 1234 and '1234' are equal.
 * [1,2] and '1,2' are equal.
 * 0 and false and '' are equal, 1 and true are equal.
 * Ignores case and trims whitespace.
 */
export function isDefinedAndDifferent(firstValue, secondValue) {
    if (undefined === firstValue) return false;
    if (firstValue === secondValue) return false;
    // eslint-disable-next-line eqeqeq
    if (firstValue == secondValue) return false;
    if ((firstValue + '').toLowerCase().trim() === (secondValue + '').toLowerCase().trim()) return false;
    return !(
        (isNullOrUndefined(firstValue) || '' === firstValue || !firstValue) &&
        (isNullOrUndefined(secondValue) || '' === secondValue)
    );
}

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @param  {boolean} compareArrays Whether to compare individual indicies of arrays. If false will just return the whole array if arrays differ
 * @return {Object}        Return a new object who represent the diff
 */
export function diff(object, base, compareArrays = false) {
    return changes(object, base, compareArrays);
}

function changes(object, base, compareArrays) {
    return transform(object, (result, value, key) => {
        if (!isEqual(value, base[key])) {
            result[key] =
                isObject(value) && isObject(base[key]) && (compareArrays || !Array.isArray(base[key]))
                    ? changes(value, base[key], compareArrays)
                    : value;
        }
    });
}

/**
 * Deep changes between two objects, using lodash. Retains IDs & array sort order.
 * @param  {Object} input  Object with all the data we want tested
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object with only what was different to the base but with all nested IDs.
 */
export function stripUnchanged(input, base) {
    // walk the object and test each value; only return changed objects - always with IDs.
    return transform(input, (result, value, key) => {
        if (!isEqual(value, base[key])) {
            if (Array.isArray(value)) {
                // tests each item in array, ignoring the array sort.
                const newArray = stripUnchangedArray(value, base[key]);
                if (newArray) result[key] = newArray;
                else delete result[key];
            } else {
                // test either for nested object or simple value.
                result[key] = isObject(value) && isObject(base[key]) ? stripUnchanged(value, base[key]) : value;
                // when nested object reference is unchanged no need to include it.
                if (key.length > 2 && key.endsWith('ID')) {
                    const nestedKey = key.slice(0, -2);
                    if (base[nestedKey] && base[nestedKey]['ID'] === value) {
                        delete result[key];
                    }
                }
            }
            // ensure every changed object has an ID.
            if (undefined === result.ID) {
                result.ID = (base && base.ID) || null;
            }
        }
    });
}

function stripUnchangedArray(input, base) {
    const changeList = input.map(obj => {
        const key = obj.ID && obj.ID > 0 && Array.isArray(base) ? base.findIndex(org => org.ID === obj.ID) : -1;
        return key < 0
            ? { ID: null, ...obj } // retains any new ID
            : { ...stripUnchanged(obj, base[key]), ID: base[key].ID }; // force the matched ID
    });
    // if arrays are same length and only contain IDs, then there's no changes - so discard.
    return !Array.isArray(base) ||
    changeList.length !== base.length ||
    changeList.some(obj => Object.keys(obj).length !== 1)
        ? changeList
        : null;
}

/**
 * This fixes values coming from the backend which should
 * be boolean true/false but are set as '1'/'0'
 */
export function fixBoolean(data, key) {
    if (data[key] === '0') data[key] = null;
    if (data[key] === '1') data[key] = true;
}

export const getElementValueWrapper = context => {
    const { value, form, name, emptyValue, customvalue } = context.props;

    //value takes precedence
    if (!isNullOrUndefined(value)) return value;

    //use form
    if (!isNullOrUndefined(form) && !stringIsNullOrEmpty(name)) return form.getState(name) || emptyValue;

    //use state
    if (!isNullOrUndefined(context.state?.value)) return context.state.value;

    //use customvalue when setting value doesnt allow you to type in the field
    if (!isNullOrUndefined(customvalue)) return customvalue;

    //use empty value
    return emptyValue;
};

/**
 * Merge changes between two objects, using lodash. Checks nested arrays for added/removed items by ID.
 * @param  {Object} originalData    Object with older data to retain if unchanged.
 * @param  {Object} newerData       Object with newer data to merge and replace.
 * @return {Object}                 Return a new object with all the new data plus the original bits unchanged.
 */
export function mergeDataSources(originalData, newerData) {
    const finalData = cloneDeep(originalData);
    if (finalData.ID === newerData.ID) {
        Object.keys(newerData).forEach(key => {
            if (Array.isArray(newerData[key])) {
                finalData[key] = mergeArraySources(Array.isArray(finalData[key]) ? finalData[key] : [], newerData[key]);
            } else if (null !== newerData[key] && isObject(newerData[key])) {
                // null is an object!
                finalData[key] = mergeDataSources(isObject(finalData[key]) ? finalData[key] : {}, newerData[key]);
            } else finalData[key] = newerData[key];
        });
        return finalData;
    }
    return Object.keys(newerData || {}).length ? cloneDeep(newerData) : finalData;
}

function mergeArraySources(originalArray, newerArray) {
    const finalArray = originalArray
        .map(oldObj => {
            const { ID } = oldObj;
            const finalObj = newerArray.find(newObj => ID === newObj.ID);
            return finalObj ? mergeDataSources(oldObj, finalObj) : null;
        })
        .filter(obj => !!obj);
    const oldIDs = finalArray.map(obj => obj.ID);
    return [].concat(
        finalArray,
        newerArray.filter(obj => !oldIDs.includes(obj.ID)).map(obj => cloneDeep(obj))
    );
}

/**
 * Repair Downshift so that user can select text & move cursor
 * @param originalGetInputProps Downshift's original function
 */
export function getDownshiftInputProps(originalGetInputProps) {
    return (userProps = {}) => {
        const downshiftProps = originalGetInputProps(userProps);
        return {
            ...downshiftProps,
            onKeyDown: event => {
                const targetKeys = ['ArrowUp', 'ArrowDown', 'Home', 'End'];
                if (event.shiftKey && targetKeys.includes(event.key)) {
                    event.nativeEvent.preventDownshiftDefault = true;
                }
                if (userProps.onKeyDown) {
                    userProps.onKeyDown(event);
                }
                if (downshiftProps.onKeyDown) {
                    downshiftProps.onKeyDown(event);
                }
            }
        };
    };
}
